const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/brand/pandaseller-logo.png?v=7`;

/**
 * Shared auth chrome — branded split layout for login, signup, SP-API.
 * Brand-first: PandaSeller leads the first viewport; form is secondary.
 */
function AuthShell({
  brandEyebrow = 'PandaSeller',
  brandTitle,
  brandBody,
  children,
}) {
  return (
    <main className="auth-shell relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-5 sm:px-6 sm:py-6">
      <div className="auth-shell__glow" aria-hidden="true" />

      <div className="auth-shell__frame relative mx-auto grid w-full max-w-[1120px] overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-white shadow-[0_32px_96px_-24px_rgba(15,23,42,0.35)] lg:h-[min(820px,calc(100vh-2.5rem))] lg:grid-cols-[1.05fr_1.1fr]">
        <aside className="auth-shell__brand relative flex h-full flex-col justify-between overflow-hidden p-8 text-white sm:p-10 lg:p-12">
          <div className="auth-shell__orb auth-shell__orb--a" aria-hidden="true" />
          <div className="auth-shell__orb auth-shell__orb--b" aria-hidden="true" />
          <div className="auth-shell__orb auth-shell__orb--c" aria-hidden="true" />
          <div className="auth-shell__grid" aria-hidden="true" />

          {/* Top Header */}
          <div className="relative z-10 flex min-h-0 flex-col">
            <div className="flex shrink-0 items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-blue-400 via-blue-600 to-indigo-800 text-xs font-black tracking-wider text-white shadow-[0_8px_20px_-6px_rgba(37,99,235,0.6)] ring-1 ring-white/25">
                  PS
                </span>
                <div>
                  <p className="font-display text-sm font-bold tracking-tight text-white">
                    {brandEyebrow}
                  </p>
                  <p className="text-xs text-blue-200/60 font-medium">Amazon seller workspace</p>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 backdrop-blur-md">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>SP-API Live</span>
              </div>
            </div>

            {/* Logo with ambient backlight */}
            <div className="auth-shell__logo relative mt-7 inline-flex shrink-0 self-start">
              <div className="absolute -inset-3 rounded-2xl bg-blue-500/20 blur-xl" aria-hidden="true" />
              <img
                src={LOGO_SRC}
                alt="PandaSeller — The Performance Hub for Modern Sellers"
                className="relative h-auto max-h-[160px] w-[min(100%,190px)] object-contain drop-shadow-[0_20px_35px_rgba(0,0,0,0.45)] transition-transform duration-300 hover:scale-[1.02]"
                width={190}
                height={140}
                decoding="async"
              />
            </div>

            {/* Brand Title & Subtitle */}
            <h1 className="auth-shell__title mt-5 text-[clamp(1.65rem,2.8vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-white">
              {brandTitle}
            </h1>
            {brandBody ? (
              <p className="mt-2.5 max-w-[38ch] text-[14px] leading-relaxed text-blue-100/75 font-normal">
                {brandBody}
              </p>
            ) : null}


          </div>

          {/* Bottom Trust & Security Badges */}
          <div className="relative z-10 mt-8 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-5 text-[11px] font-medium text-blue-200/65">
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Role-Based Access
            </span>
            <span className="text-white/20">•</span>
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              99.9% Uptime SLA
            </span>
            <span className="text-white/20">•</span>
            <span>Amazon SP-API Certified</span>
          </div>
        </aside>

        <section className="relative grid h-full place-items-center overflow-y-auto bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-6 sm:p-10 lg:p-12">
          <div className="auth-shell__card w-full max-w-[27rem]">{children}</div>
        </section>
      </div>
    </main>
  );
}

export default AuthShell;
